"use client";

import React from "react";
import { message } from "antd";
import {
  programsStudentAcademy,
  programsStudentModuleTechniqueAcademy,
  programsModuleAcademy,
  programsTopicsAcademy,
  programsModuleTechniquesAcademy,
} from "../../../../shared/services";
import {
  ProgramModuleType,
  ProgramStudentType,
  ProgramTechniquesType,
  ProgramTopicType,
} from "../../../../types/dto/program";
import { fetchAllPaginated } from "../../../../shared/utils/fetchAllPaginated";

export interface StudentQualificationRow {
  userId: number;
  userName: string;
  studentCode: string | undefined;
  qualificationId: number | null;
  qualification: number | null;
  qualification_check: "approved" | "disapproved" | null;
  hasQualification: boolean;
  statusChanged: boolean;
  saving: boolean;
}

async function persistStudentQualification(
  row: StudentQualificationRow,
  techniqueId: number,
  data: {
    qualification?: number | null;
    qualification_check?: "approved" | "disapproved" | null;
  }
) {
  if (row.qualificationId) {
    return programsStudentModuleTechniqueAcademy.patch(row.qualificationId, data);
  }

  return programsStudentModuleTechniqueAcademy.create({
    user_id: row.userId,
    program_module_technique_id: techniqueId,
    ...data,
  });
}

export default function useBulkQualifications(programId: number) {
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  const [modules, setModules] = React.useState<ProgramModuleType[]>([]);
  const [topics, setTopics] = React.useState<ProgramTopicType[]>([]);
  const [techniques, setTechniques] = React.useState<ProgramTechniquesType[]>([]);

  const [selectedModuleId, setSelectedModuleId] = React.useState<number | null>(null);
  const [selectedTopicId, setSelectedTopicId] = React.useState<number | null>(null);
  const [selectedTechniqueId, setSelectedTechniqueId] = React.useState<number | null>(null);

  const [rows, setRows] = React.useState<StudentQualificationRow[]>([]);
  const [selectedUserIds, setSelectedUserIds] = React.useState<number[]>([]);

  React.useEffect(() => {
    if (!programId) return;
    programsModuleAcademy
      .find({ query: { program_id: programId, $limit: 1000, $sort: { position: 1 } } })
      .then((res: any) => setModules(res.data))
      .catch(console.error);
  }, [programId]);

  React.useEffect(() => {
    setTopics([]);
    setSelectedTopicId(null);
    setSelectedTechniqueId(null);
    setRows([]);
    setSelectedUserIds([]);
    if (!selectedModuleId) return;
    programsTopicsAcademy
      .find({ query: { program_module_id: selectedModuleId, $limit: 1000 } })
      .then((res: any) => setTopics(res.data))
      .catch(console.error);
  }, [selectedModuleId]);

  React.useEffect(() => {
    setTechniques([]);
    setSelectedTechniqueId(null);
    setRows([]);
    setSelectedUserIds([]);
    if (!selectedTopicId) return;
    programsModuleTechniquesAcademy
      .find({ query: { program_module_topic_id: selectedTopicId, $limit: 1000 } })
      .then((res: any) => setTechniques(res.data))
      .catch(console.error);
  }, [selectedTopicId]);

  const loadStudentsAndQualifications = React.useCallback(async () => {
    if (!selectedTechniqueId || !programId) return;
    setLoading(true);
    setSelectedUserIds([]);
    try {
      const studentsPromise = fetchAllPaginated<ProgramStudentType>(
        (query) => programsStudentAcademy.find({ query }),
        {
          program_id: programId,
          $sort: { id: -1 },
        }
      );

      const qualificationsPromise = fetchAllPaginated<any>(
        (query) =>
          programsStudentModuleTechniqueAcademy.find({ query }),
        {
          program_module_technique_id: selectedTechniqueId,
          $sort: { id: -1 },
          $client: { skipUserJoin: "true" },
        }
      ).catch((err) => {
        console.error("Error cargando calificaciones existentes:", err);
        message.warning(
          "No se pudieron cargar algunas calificaciones previas. Los estudiantes se mostrarán sin estado."
        );
        return [] as any[];
      });

      const [studentsData, qualificationsData] = await Promise.all([
        studentsPromise,
        qualificationsPromise,
      ]);

      const qualMap: Record<number, any> = {};
      for (const q of qualificationsData) {
        qualMap[q.user_id] = q;
      }

      const studentsByUserId = new Map<number, ProgramStudentType>();
      for (const student of studentsData) {
        const studentProgramId = Number(
          student.program_id ?? student.program?.id
        );
        if (studentProgramId && studentProgramId !== programId) continue;

        const existing = studentsByUserId.get(student.user_id);
        if (
          !existing ||
          (student.status === "active" && existing.status !== "active")
        ) {
          studentsByUserId.set(student.user_id, student);
        }
      }

      const newRows: StudentQualificationRow[] = Array.from(
        studentsByUserId.values()
      )
        .map((s: ProgramStudentType) => ({
          userId: s.user_id,
          userName: `${s.user?.first_name ?? ""} ${s.user?.last_name ?? ""}`.trim(),
          studentCode: s.user?.student_code ?? undefined,
          qualificationId: qualMap[s.user_id]?.id ?? null,
          qualification: qualMap[s.user_id]?.qualification ?? null,
          qualification_check: qualMap[s.user_id]?.qualification_check ?? null,
          hasQualification: !!qualMap[s.user_id],
          statusChanged: false,
          saving: false,
        }))
        .sort((a: StudentQualificationRow, b: StudentQualificationRow) =>
          a.userName.localeCompare(b.userName, "es", { sensitivity: "base" })
        );
      setRows(newRows);
    } catch (err: any) {
      console.error(err);
      message.error(err?.message ?? "Error al cargar los estudiantes del programa");
    } finally {
      setLoading(false);
    }
  }, [selectedTechniqueId, programId]);

  React.useEffect(() => {
    loadStudentsAndQualifications();
  }, [loadStudentsAndQualifications]);

  const allUserIds = React.useMemo(() => rows.map((r) => r.userId), [rows]);

  const isAllSelected =
    rows.length > 0 && selectedUserIds.length === rows.length;
  const isIndeterminate =
    selectedUserIds.length > 0 && selectedUserIds.length < rows.length;

  const toggleRow = (userId: number, checked: boolean) => {
    setSelectedUserIds((prev) =>
      checked ? [...prev, userId] : prev.filter((id) => id !== userId)
    );
  };

  const setSelectAll = (checked: boolean, scopeUserIds?: number[]) => {
    const ids = scopeUserIds ?? allUserIds;
    if (!checked) {
      if (scopeUserIds) {
        setSelectedUserIds((prev) =>
          prev.filter((id) => !scopeUserIds.includes(id))
        );
      } else {
        setSelectedUserIds([]);
      }
      return;
    }
    setSelectedUserIds((prev) => {
      const merged = [...prev];
      ids.forEach((id) => {
        if (!merged.includes(id)) merged.push(id);
      });
      return merged;
    });
  };

  const updateRow = (
    userId: number,
    field: "qualification" | "qualification_check",
    value: any
  ) => {
    setRows((prev) =>
      prev.map((r) =>
        r.userId === userId
          ? { ...r, [field]: value, statusChanged: field === "qualification_check" ? true : r.statusChanged }
          : r
      )
    );
  };

  const saveRow = async (
    userId: number,
    qualification_check: "approved" | "disapproved" | null
  ) => {
    if (!selectedTechniqueId) return;

    let previousCheck: StudentQualificationRow["qualification_check"] = null;
    let currentRow: StudentQualificationRow | undefined;
    setRows((prev) => {
      currentRow = prev.find((r) => r.userId === userId);
      previousCheck = currentRow?.qualification_check ?? null;
      return prev.map((r) =>
        r.userId === userId
          ? { ...r, qualification_check, statusChanged: false, saving: true }
          : r
      );
    });

    if (!currentRow) return;

    try {
      const response = await persistStudentQualification(
        currentRow,
        selectedTechniqueId,
        { qualification_check }
      );
      setRows((prev) =>
        prev.map((r) =>
          r.userId === userId
            ? {
                ...r,
                saving: false,
                hasQualification: true,
                qualificationId: response.id ?? r.qualificationId,
              }
            : r
        )
      );
      message.success(
        currentRow.qualificationId
          ? "Calificación actualizada correctamente"
          : "Calificación creada correctamente"
      );
    } catch (err: any) {
      setRows((prev) =>
        prev.map((r) =>
          r.userId === userId
            ? { ...r, qualification_check: previousCheck, saving: false }
            : r
        )
      );
      message.error(err.message ?? "Error al guardar");
    }
  };

  const applyToSelected = (
    status: "approved" | "disapproved" | null,
    onlySelected = true
  ) => {
    const targetIds = onlySelected ? selectedUserIds : allUserIds;
    if (targetIds.length === 0) {
      message.warning("Selecciona al menos un estudiante");
      return;
    }
    setRows((prev) =>
      prev.map((r) =>
        targetIds.includes(r.userId)
          ? { ...r, qualification_check: status, statusChanged: true }
          : r
      )
    );
  };

  const selectedRows = rows.filter((r) => selectedUserIds.includes(r.userId));

  const readyToSaveCount = selectedRows.filter(
    (r) => r.qualification !== null || r.qualification_check !== null || r.statusChanged
  ).length;

  const saveAll = async () => {
    if (!selectedTechniqueId) return;
    if (selectedUserIds.length === 0) {
      message.warning("Selecciona al menos un estudiante para guardar");
      return;
    }
    const toSave = selectedRows.filter(
      (r) => r.qualification !== null || r.qualification_check !== null || r.statusChanged
    );
    if (toSave.length === 0) {
      message.warning(
        "Los estudiantes seleccionados no tienen cambios para guardar"
      );
      return;
    }
    setSaving(true);
    try {
      await Promise.all(
        toSave.map((row) =>
          persistStudentQualification(row, selectedTechniqueId, {
            ...(row.qualification !== null && { qualification: row.qualification }),
            ...(row.statusChanged && {
              qualification_check: row.qualification_check,
            }),
          })
        )
      );
      const createdCount = toSave.filter((row) => !row.qualificationId).length;
      const updatedCount = toSave.length - createdCount;
      const summary = [
        createdCount > 0 ? `${createdCount} creada(s)` : null,
        updatedCount > 0 ? `${updatedCount} actualizada(s)` : null,
      ]
        .filter(Boolean)
        .join(" y ");
      message.success(`Calificaciones guardadas: ${summary}.`);
      loadStudentsAndQualifications();
    } catch (err: any) {
      message.error(err.message ?? "Error al guardar calificaciones");
    } finally {
      setSaving(false);
    }
  };

  return {
    loading,
    saving,
    modules,
    topics,
    techniques,
    selectedModuleId,
    selectedTopicId,
    selectedTechniqueId,
    rows,
    selectedUserIds,
    isAllSelected,
    isIndeterminate,
    readyToSaveCount,
    setSelectedModuleId,
    setSelectedTopicId,
    setSelectedTechniqueId,
    toggleRow,
    setSelectAll,
    updateRow,
    saveRow,
    applyToSelected,
    saveAll,
  };
}
